import { useEffect, useState } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { FiTrash } from 'react-icons/fi';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InputSelect, PopOver, SideSliderV2 } from '@wajeez-web/components/ui';

import {
  dateAndNumberComparisonValues,
  stringComparisonValues,
  otherComparisonValues,
} from './filterOperators';
import { Select, Dropdown } from '../..';
import { useFilterInput } from './useFilterInput';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FormField } from '@wajeez-web/components/form-builder';

export const selectFilterOperators = (
  dataType: Server.DataType | undefined
) => {
  if (
    dataType === Server.DataType.Date ||
    dataType === Server.DataType.DateTime ||
    dataType === Server.DataType.Number
  ) {
    return dateAndNumberComparisonValues;
  } else if (dataType === Server.DataType.String) {
    return stringComparisonValues;
  }

  return otherComparisonValues;
};

export type FilterOption = {
  key: string | undefined;
  label: string | undefined;
  dataType: Server.DataType | undefined;
  operator?: Server.ComparisonType | undefined;
  value?: string | undefined;
  onChange?: (value: string | undefined) => void;
  options?: string[] | undefined;
};

interface TableFilterProps {
  filters: FilterOption[] | undefined;
  shownFilters: FilterOption[] | undefined;
  setShownFilters: React.Dispatch<
    React.SetStateAction<FilterOption[] | undefined>
  >;
  selectedFilters: {
    filter: FilterOption;
    operators: { value: string; label: string }[] | null;
  }[];
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<
      {
        filter: FilterOption;
        operators: { value: string; label: string }[] | null;
      }[]
    >
  >;
  appliedFilters: FilterOption[];
  setAppliedFilters: React.Dispatch<React.SetStateAction<FilterOption[]>>;
  onChange: (filters: FilterOption[]) => void;
  defaultFilters?: {
    key: string;
    operator: Server.ComparisonType;
    dataType: Server.DataType;
    label: string;
    fieldMeta: FormField;
    onChange?: (value: any) => void;
  }[];
}

export const TableFilter = ({
  filters,
  shownFilters,
  setShownFilters,
  selectedFilters,
  setSelectedFilters,
  appliedFilters,
  setAppliedFilters,
  onChange,
  defaultFilters,
}: TableFilterProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { renderFilterInput, applyFilter, applyFilterRequest } = useFilterInput(
    appliedFilters,
    setAppliedFilters,
    onChange
  );

  useEffect(() => {
    if (!selectedFilters.length) {
      setIsSidebarOpen(false);
    }
  }, [selectedFilters.length]);

  const renderDefaultFilters = () => {
    return defaultFilters?.map((f) => {
      if (f.fieldMeta.fieldType === Server.FieldType.Select) {
        return (
          <PopOver
            key={f.key}
            label="Type"
            name="Type"
            shape="squre"
            panelStyle="w-52"
          >
            <InputSelect
              isMenuOpen={true}
              options={f.fieldMeta.fieldOptions.selectOptions}
              onChange={(name, value: any) => {
                if (value) {
                  setSelectedFilters(
                    selectedFilters.filter((ssf) => ssf.filter.key !== f.key)
                  );

                  const foundSelectedFilter = selectedFilters.find(
                    (ff) => ff.filter.key === f.key
                  );
                  const foundAppliedFilter = appliedFilters.find(
                    (ff) => ff.key === f.key
                  );

                  if (!foundSelectedFilter) {
                    f.dataType !== undefined &&
                      setSelectedFilters([
                        ...selectedFilters,
                        {
                          filter: {
                            key: f.key,
                            dataType: f.dataType,
                            label: f.label,
                            operator: f.operator,
                          },
                          operators: selectFilterOperators(f.dataType),
                        },
                      ]);
                  } else {
                    setSelectedFilters([...selectedFilters]);
                  }

                  if (!foundAppliedFilter) {
                    setAppliedFilters(
                      appliedFilters.filter((af) => af.key !== f.key)
                    );
                  } else {
                    setAppliedFilters(
                      appliedFilters.map((a) => {
                        if (a.key !== foundAppliedFilter.key) {
                          return a;
                        } else {
                          return {
                            ...a,
                            value,
                          };
                        }
                      })
                    );
                  }

                  setShownFilters(
                    shownFilters?.filter((sf) => sf.key !== f.key)
                  );

                  applyFilter(value.value, {
                    key: f.key,
                    dataType: f.dataType,
                    label: f.label,
                    operator: f.operator,
                  });

                  applyFilterRequest(value.value, {
                    key: f.key,
                    dataType: f.dataType,
                    label: f.label,
                    operator: f.operator,
                  });

                  f.onChange && f.onChange(value.value);
                }
              }}
              name="Type"
            />
          </PopOver>
        );
      } else {
        return null;
      }
    });
  };

  const renderSelectedFilters = () => {
    return selectedFilters.map((sf) => {
      const CloseButton = (
        <div
          className="text-[16px] cursor-pointer hover:text-red-500 active:text-black"
          onClick={() => {
            shownFilters &&
              setShownFilters(
                filters?.filter((h) => {
                  let isFilterApplied = false;

                  for (const af of appliedFilters) {
                    if (af.key === h.key) {
                      isFilterApplied = true;

                      break;
                    }
                  }

                  return !isFilterApplied || h.key === sf.filter.key;
                })
              );

            setSelectedFilters(
              selectedFilters.filter((ssf) => ssf.filter.key !== sf.filter.key)
            );

            setAppliedFilters(
              appliedFilters.filter((af) => af.key !== sf.filter.key)
            );

            onChange(appliedFilters.filter((af) => af.key !== sf.filter.key));
          }}
        >
          <FiTrash />
        </div>
      );

      return (
        <div
          key={sf.filter.key}
          className="w-full p-4 flex justify-start items-center space-s-3 border rounded"
        >
          <div className="w-full">
            <div className="w-[100px] text-[16px] font-medium">
              {sf.filter.label}
            </div>
            <div className="mt-2 w-full flex justify-start items-center space-s-3">
              <div className="w-fit max-w-[300px] h-[30px]">
                <Select
                  name="select-filter-operator"
                  options={
                    sf.operators?.map((s) => {
                      return { value: s.value, label: s.label };
                    }) || []
                  }
                  onChange={(option) => {
                    const optionValue = option as {
                      value: string;
                      label: string;
                    };

                    const newFilter = {
                      ...sf,
                      filter: {
                        ...sf.filter,
                        operator: Number(optionValue.value),
                      },
                    };

                    setSelectedFilters(
                      selectedFilters.map((ssf) => {
                        if (ssf.filter.key === sf.filter.key) {
                          return newFilter;
                        } else {
                          return ssf;
                        }
                      })
                    );

                    const foundAppliedFilter = appliedFilters.find(
                      (af) => af.key === sf.filter.key
                    );

                    if (foundAppliedFilter) {
                      applyFilter(foundAppliedFilter.value, newFilter.filter);

                      applyFilterRequest(
                        foundAppliedFilter.value,
                        newFilter.filter
                      );
                    }
                  }}
                  option={sf.operators?.find(
                    (o) => Number(o.value) === sf.filter.operator
                  )}
                />
              </div>
              {sf.filter.operator !== undefined ? (
                <div className="w-full max-w-[500px]">
                  {renderFilterInput(sf.filter)}
                </div>
              ) : null}
            </div>
          </div>
          <div className="">{CloseButton}</div>
        </div>
      );
    });
  };

  return (
    <div className="w-full">
      <div className="w-full">
        <Dropdown
          id="add-filter"
          items={
            shownFilters?.map((f) => {
              return {
                label: f.label || '',
                onClick: () => {
                  f.dataType !== undefined &&
                    setSelectedFilters([
                      ...selectedFilters,
                      {
                        filter: f,
                        operators: selectFilterOperators(f.dataType),
                      },
                    ]);

                  setShownFilters(
                    shownFilters.filter((sf) => sf.key !== f.key)
                  );

                  setIsSidebarOpen(true);
                },
              };
            }) || []
          }
          label={
            <div className="w-full flex justify-center items-center space-s-1 text-black">
              <div>
                <ReturnTranslatedText
                  origintext="Add Filter"
                  text={`buttons.${'Add Filter'}`}
                />
              </div>
              <div>
                <AiOutlinePlus />
              </div>
            </div>
          }
          buttonStyle={{ color: 'black' }}
        />
      </div>
      <div className="mt-3">{renderDefaultFilters()}</div>
      {selectedFilters.length ? (
        <div
          className="w-fit mt-3 flex justify-start items-center cursor-pointer text-[14px] text-primary"
          onClick={(e) => {
            setIsSidebarOpen(true);
          }}
        >
          <ReturnTranslatedText
            origintext="Selected filters"
            text={`general.${'Selected filters'}`}
          />
          <div>{': ' + selectedFilters.length}</div>
        </div>
      ) : null}
      <div className="">
        <SideSliderV2
          show={isSidebarOpen}
          title="Filters"
          onClose={() => {
            setIsSidebarOpen(false);
          }}
        >
          <div className="h-full w-full px-4 pb-4 overflow-y-auto">
            {selectedFilters.length ? (
              <div
                className="mt-4 py-1 px-2 w-fit text-[14px] font-medium text-[#6c757d] cursor-pointer hover:bg-[#ced3d93e] active:bg-transparent rounded-[8px]"
                onClick={() => {
                  setShownFilters(filters);
                  setSelectedFilters([]);
                  setAppliedFilters([]);

                  onChange([]);
                }}
              >
                <ReturnTranslatedText
                  origintext="Clear All"
                  text={`general.${'Clear All'}`}
                />
              </div>
            ) : null}
            <div className="mt-3 w-full flex flex-col space-y-4">
              {renderSelectedFilters()}
            </div>
          </div>
        </SideSliderV2>
      </div>
    </div>
  );
};
